// frontend/src/screens/admin/Dashboard/AgencyDashboard.jsx

import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, FormControl, InputLabel, Select,
  MenuItem, TextField, Chip, Divider, Tooltip, CircularProgress,
  Alert, Button, IconButton,
} from '@mui/material';
import RefreshIcon              from '@mui/icons-material/Refresh';
import PrintIcon                from '@mui/icons-material/Print';
import AssessmentIcon           from '@mui/icons-material/Assessment';
import CheckCircleOutlineIcon   from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon       from '@mui/icons-material/CancelOutlined';
import EventBusyIcon            from '@mui/icons-material/EventBusy';
import EventAvailableIcon       from '@mui/icons-material/EventAvailable';
import BusinessCenterIcon       from '@mui/icons-material/BusinessCenter';
import LocationCityIcon         from '@mui/icons-material/LocationCity';
import ReceiptLongIcon          from '@mui/icons-material/ReceiptLong';
import WorkspacePremiumIcon     from '@mui/icons-material/WorkspacePremium';
import GroupIcon                from '@mui/icons-material/Group';
import LocalHospitalIcon        from '@mui/icons-material/LocalHospital';

import { useGetAgencyShiftsQuery } from '../../../slices/shiftApiSlice';
import { useGetAgenciesQuery } from '../../../slices/agencyApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

// ─── Palette ─────────────────────────────────────────────────────────────────
const NAVY   = '#0d1b2a';
const TEAL   = '#0a8577';
const TEAL_L = '#0cb89e';
const AMBER  = '#e8a020';
const RED    = '#c0392b';
const GREEN  = '#27ae60';
const SLATE  = '#1e2d3d';
const MUTED  = '#8fa3b1';
const WHITE  = '#f0f4f8';
const BLUE   = '#2196f3';

const TIER_COLORS = { Tier1: GREEN, Tier2: BLUE, Tier3: AMBER, Tier4: RED, Unspecified: MUTED };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pct = (n, d) => (d === 0 ? 0 : Math.round((n / d) * 100));
const fmt = (mins) => {
  if (!mins) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const shiftMins = (shift) => {
  if (!shift.startDateTime || !shift.endDateTime) return 0;
  return Math.round((new Date(shift.endDateTime) - new Date(shift.startDateTime)) / 60000);
};

const getShiftStatus = (shift) => {
  if (shift.agencyCancelled) return 'Cancelled';
  if (shift.actualStatus === 'Complete') return 'Complete';
  if (shift.actualStatus === 'Not-Worked') return 'Not-Worked';
  return 'Open / Assigned';
};

const monthKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

// ─── Sub-components (shared visual language with ManagerDashboard) ──────────
const SectionTitle = ({ icon: Icon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, mt: 1 }}>
    <Box sx={{ width: 4, height: 28, bgcolor: TEAL, borderRadius: 1 }} />
    <Icon sx={{ color: TEAL, fontSize: 22 }} />
    <Typography sx={{ fontFamily: '"DM Mono", monospace', fontWeight: 700,
      fontSize: '0.85rem', letterSpacing: 2, textTransform: 'uppercase', color: WHITE }}>
      {label}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, sub, color = TEAL, icon: Icon }) => (
  <Paper elevation={0} sx={{
    bgcolor: SLATE, border: `1px solid ${color}22`, borderRadius: 2,
    p: 2.5, position: 'relative', overflow: 'hidden', height: '100%',
    '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: color },
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}22` },
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: '"DM Mono", monospace',
          letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, mb: 1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontFamily: '"DM Mono", monospace',
          fontWeight: 700, color: WHITE, lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.75, fontFamily: '"DM Mono", monospace' }}>
            {sub}
          </Typography>
        )}
      </Box>
      {Icon && <Icon sx={{ color: `${color}99`, fontSize: 28 }} />}
    </Box>
  </Paper>
);

const BarRow = ({ label, value, total, color, showPct = true, badge }) => {
  const p = pct(value, total);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '65%' }}>
          <Typography sx={{ fontSize: '0.75rem', color: WHITE, fontFamily: '"DM Mono", monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
          {badge && (
            <Chip label={badge} size="small" sx={{
              height: 16, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace',
              bgcolor: `${TIER_COLORS[badge] || MUTED}22`, color: TIER_COLORS[badge] || MUTED,
              border: `1px solid ${TIER_COLORS[badge] || MUTED}44`,
            }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: WHITE, fontFamily: '"DM Mono", monospace' }}>{value}</Typography>
          {showPct && (
            <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace', minWidth: 36, textAlign: 'right' }}>
              {p}%
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ height: 6, bgcolor: `${color}22`, borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${p}%`, bgcolor: color, borderRadius: 1, transition: 'width 0.6s ease' }} />
      </Box>
    </Box>
  );
};

const StackedBarRow = ({ label, complete, cancelled, other, total, badge, labelColor = WHITE }) => (
  <Box sx={{ mb: 1.6 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '60%' }}>
        <Typography sx={{ fontSize: '0.72rem', color: labelColor, fontFamily: '"DM Mono", monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
        {badge && (
          <Chip label={badge} size="small" sx={{
            height: 16, fontSize: '0.55rem', fontFamily: '"DM Mono", monospace',
            bgcolor: `${TIER_COLORS[badge] || MUTED}22`, color: TIER_COLORS[badge] || MUTED,
            border: `1px solid ${TIER_COLORS[badge] || MUTED}44`,
          }} />
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', color: GREEN, fontFamily: '"DM Mono", monospace' }}>{complete}c</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: RED, fontFamily: '"DM Mono", monospace' }}>{cancelled}x</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: '"DM Mono", monospace' }}>{total}t</Typography>
      </Box>
    </Box>
    <Box sx={{ height: 6, bgcolor: `${TEAL}22`, borderRadius: 1, overflow: 'hidden', display: 'flex' }}>
      <Box sx={{ width: `${pct(complete, total)}%`, bgcolor: GREEN }} />
      <Box sx={{ width: `${pct(cancelled, total)}%`, bgcolor: RED }} />
      <Box sx={{ width: `${pct(other, total)}%`, bgcolor: AMBER }} />
    </Box>
  </Box>
);

const GaugeRing = ({ value, label, color = TEAL, size = 100 }) => {
  const r    = 40;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke={`${color}22`} strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 50 50)" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
        <text x="50" y="54" textAnchor="middle" fill={WHITE}
          style={{ fontSize: 18, fontFamily: '"DM Mono", monospace', fontWeight: 700 }}>
          {value}%
        </text>
      </svg>
      <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: '"DM Mono", monospace',
        textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
};

const MonthlyTrendChart = ({ data, trackHeight = 150 }) => {
  const maxTotal = Math.max(1, ...data.map(([, d]) => d.total));
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 0.5, sm: 1.5 }, height: trackHeight + 50, px: 1, overflowX: 'auto' }}>
      {data.map(([label, d]) => {
        const totalPx     = (d.total     / maxTotal) * trackHeight;
        const completePx  = (d.complete  / maxTotal) * trackHeight;
        const cancelledPx = (d.cancelled / maxTotal) * trackHeight;
        const otherPx     = (d.other     / maxTotal) * trackHeight;
        return (
          <Box key={label} sx={{ flex: '1 0 auto', minWidth: 34, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.65rem', color: WHITE, fontFamily: '"DM Mono",monospace', mb: 0.5, fontWeight: 700 }}>
              {d.total}
            </Typography>
            <Box sx={{ height: trackHeight, width: '100%', maxWidth: 34, display: 'flex', alignItems: 'flex-end' }}>
              <Tooltip title={`${d.total} shifts · ${d.complete} complete · ${d.cancelled} cancelled`} arrow>
                <Box sx={{ width: '100%', height: `${totalPx}px`, display: 'flex', flexDirection: 'column-reverse',
                  borderRadius: '3px 3px 0 0', overflow: 'hidden', cursor: 'default',
                  bgcolor: totalPx === 0 ? `${TEAL}22` : 'transparent' }}>
                  <Box sx={{ height: `${completePx}px`, bgcolor: GREEN, width: '100%' }} />
                  <Box sx={{ height: `${cancelledPx}px`, bgcolor: RED, width: '100%' }} />
                  <Box sx={{ height: `${otherPx}px`, bgcolor: AMBER, width: '100%' }} />
                </Box>
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: '"DM Mono",monospace', mt: 0.75, whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};


const FilterSelect = ({ label, value, onChange, options, disabled }) => (
  <FormControl size="small" sx={{ minWidth: 170,
    '& .MuiOutlinedInput-root': { bgcolor: SLATE, color: WHITE, borderRadius: 1.5,
      '& fieldset': { borderColor: `${TEAL}44` },
      '&:hover fieldset': { borderColor: TEAL },
      '&.Mui-focused fieldset': { borderColor: TEAL },
    },
    '& .MuiInputLabel-root': { color: MUTED },
    '& .MuiSvgIcon-root': { color: MUTED },
  }}>
    <InputLabel sx={{ color: MUTED, fontSize: '0.8rem' }}>{label}</InputLabel>
    <Select value={value} label={label} onChange={e => onChange(e.target.value)} disabled={disabled}
      sx={{ fontSize: '0.8rem', fontFamily: '"DM Mono", monospace' }}
      MenuProps={{
        PaperProps: {
          sx: {
            bgcolor: SLATE, border: `1px solid ${TEAL}44`,
            '& .MuiMenuItem-root': {
              color: WHITE, fontFamily: '"DM Mono", monospace', fontSize: '0.8rem',
              '&:hover': { bgcolor: `${TEAL}44`, color: WHITE },
              '&.Mui-selected': { bgcolor: `${TEAL}33`, color: TEAL_L, '&:hover': { bgcolor: `${TEAL}55` } },
            },
          },
        },
      }}
    >
      <MenuItem value=""><em style={{ color: MUTED }}>All</em></MenuItem>
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const AgencyDashboard = () => {
  const now          = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth  = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [dateFrom,      setDateFrom]      = useState(firstOfMonth);
  const [dateTo,        setDateTo]        = useState(lastOfMonth);
  const [fDirectorate,  setFDirectorate]  = useState('');
  const [fWard,         setFWard]         = useState('');
  const [fAgency,       setFAgency]       = useState('');
  const [fTier,         setFTier]         = useState('');

  const startDate = dateFrom || firstOfMonth;
  const endDate   = dateTo   || lastOfMonth;

  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery();
  const { data: agenciesData     = [] } = useGetAgenciesQuery({});
  const {
    data: shiftsRaw = [], isLoading, refetch,
  } = useGetAgencyShiftsQuery({ startDate, endDate });

  // ── Lookup maps ────────────────────────────────────────────────────────────
  const wardToDirectorateMap = useMemo(() => {
    const map = {};
    hospitalLocations.forEach(loc => {
      if (loc.wardDepartmentArea && loc.directorate) map[loc.wardDepartmentArea] = loc.directorate;
    });
    return map;
  }, [hospitalLocations]);

  // Agency id → { name, tier } — the primary linkage now that shifts carry
  // filledByAgency directly, either as a populated object or a plain id.
  const agencyIdToInfo = useMemo(() => {
    const map = {};
    agenciesData.forEach(a => {
      if (a._id) map[a._id] = { name: a.agencyName || 'Unknown Agency', tier: a.agencyTier || 'Unspecified' };
    });
    return map;
  }, [agenciesData]);

  // Fallback for any shift that doesn't carry filledByAgency (older records) —
  // match on the stored name string the way AgencyShiftsListScreen does.
  const agencyNameToTier = useMemo(() => {
    const map = {};
    agenciesData.forEach(a => { if (a.agencyName) map[a.agencyName] = a.agencyTier || 'Unspecified'; });
    return map;
  }, [agenciesData]);

  // ── Filter option lists ────────────────────────────────────────────────────
  const directorateOptions = useMemo(
    () => [...new Set(hospitalLocations.map(l => l.directorate).filter(Boolean))].sort(),
    [hospitalLocations]
  );
  const wardOptions = useMemo(() => {
    const locs = fDirectorate ? hospitalLocations.filter(l => l.directorate === fDirectorate) : hospitalLocations;
    return [...new Set(locs.map(l => l.wardDepartmentArea).filter(Boolean))].sort();
  }, [hospitalLocations, fDirectorate]);
  const agencyOptions = useMemo(
    () => [...new Set(agenciesData.map(a => a.agencyName).filter(Boolean))].sort(),
    [agenciesData]
  );
  const tierOptions = ['Tier1', 'Tier2', 'Tier3', 'Tier4'];

  // ── Enrich raw shifts with derived fields ─────────────────────────────────
  const enrichedShifts = useMemo(() => (shiftsRaw || []).map(s => {
    // filledByAgency may come back as a populated object ({_id, agencyName,
    // agencyTier, ...}) or as a plain id string depending on the query —
    // handle both.
    const agencyId = (s.filledByAgency && typeof s.filledByAgency === 'object')
      ? s.filledByAgency._id
      : s.filledByAgency;
    const agencyById = agencyId ? agencyIdToInfo[agencyId] : null;

    const fallbackName = s.actualFilledByAgencyName || s.filledByAgencyName || 'Unknown Agency';
    const agencyName   = agencyById?.name || fallbackName;
    const tier         = agencyById?.tier || agencyNameToTier[agencyName] || 'Unspecified';

    return {
      ...s,
      _agencyId: agencyId || null,
      _agencyName: agencyName,
      _workerName: s.actualFilledByAgencyWorkerName || s.filledByAgencyWorkerName || 'N/A',
      _directorate: wardToDirectorateMap[s.wardDepartmentArea] || 'Unassigned',
      _tier: tier,
      _status: getShiftStatus(s),
      _mins: shiftMins(s),
    };
  }), [shiftsRaw, wardToDirectorateMap, agencyIdToInfo, agencyNameToTier]);

  const filteredShifts = useMemo(() => enrichedShifts.filter(s => {
    if (fDirectorate && s._directorate !== fDirectorate)       return false;
    if (fWard        && s.wardDepartmentArea !== fWard)         return false;
    if (fAgency       && s._agencyName !== fAgency)              return false;
    if (fTier         && s._tier !== fTier)                      return false;
    return true;
  }), [enrichedShifts, fDirectorate, fWard, fAgency, fTier]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total     = filteredShifts.length;
    const complete  = filteredShifts.filter(s => s._status === 'Complete');
    const cancelled = filteredShifts.filter(s => s._status === 'Cancelled');
    const notWorked = filteredShifts.filter(s => s._status === 'Not-Worked');
    const open      = filteredShifts.filter(s => s._status === 'Open / Assigned');

    const totalMins    = filteredShifts.reduce((a, s) => a + s._mins, 0);
    const completeMins = complete.reduce((a, s) => a + s._mins, 0);

    const withPO   = filteredShifts.filter(s => s.poNumber && String(s.poNumber).trim() !== '');
    const missingPO = total - withPO.length;

    const groupBy = (keyFn) => Object.entries(
      filteredShifts.reduce((acc, s) => {
        const k = keyFn(s) || 'Unknown';
        if (!acc[k]) acc[k] = { total: 0, complete: 0, cancelled: 0, other: 0, tier: s._tier };
        acc[k].total++;
        if (s._status === 'Complete')       acc[k].complete++;
        else if (s._status === 'Cancelled') acc[k].cancelled++;
        else                                 acc[k].other++;
        return acc;
      }, {})
    ).sort((a, b) => b[1].total - a[1].total);

    const byTier        = groupBy(s => s._tier);
    const byAgency       = groupBy(s => s._agencyName).slice(0, 8);
    const byDirectorate  = groupBy(s => s._directorate);
    const byWard         = groupBy(s => s.wardDepartmentArea).slice(0, 8);
    const byCategory     = groupBy(s => s.categoryOfStaff);
    const byReason       = groupBy(s => s.reasonForRequest);

    // Clinical-reason sub-breakdown for the two sensitive request types —
    // same convention as the Manager Dashboard's specials/cohorts sections,
    // just using this dashboard's complete/cancelled/other categories
    // instead of staff/agency, since every shift here is already agency-sourced.
    const groupBySub = (list, keyFn) => Object.entries(
      list.reduce((acc, s) => {
        const k = keyFn(s) || '⚠ Not recorded';
        if (!acc[k]) acc[k] = { total: 0, complete: 0, cancelled: 0, other: 0 };
        acc[k].total++;
        if (s._status === 'Complete')       acc[k].complete++;
        else if (s._status === 'Cancelled') acc[k].cancelled++;
        else                                 acc[k].other++;
        return acc;
      }, {})
    ).sort((a, b) => b[1].total - a[1].total);

    const specialsShifts = filteredShifts.filter(s => (s.reasonForRequest || '').toLowerCase() === '1-1 special');
    const cohortsShifts  = filteredShifts.filter(s => (s.reasonForRequest || '').toLowerCase() === 'cohorts');
    const specialsBreakdown = groupBySub(specialsShifts, s => s.specialsReason);
    const cohortsBreakdown  = groupBySub(cohortsShifts,  s => s.specialsReason);

    return {
      total,
      complete: complete.length, cancelled: cancelled.length,
      notWorked: notWorked.length, open: open.length,
      totalMins, completeMins,
      pctComplete:  pct(complete.length,  total),
      pctCancelled: pct(cancelled.length, total),
      pctNotWorked: pct(notWorked.length, total),
      pctOpen:      pct(open.length,      total),
      withPOCount: withPO.length, missingPO, pctPO: pct(withPO.length, total),
      uniqueAgencies:     new Set(filteredShifts.map(s => s._agencyName)).size,
      uniqueWards:        new Set(filteredShifts.map(s => s.wardDepartmentArea).filter(Boolean)).size,
      uniqueDirectorates: new Set(filteredShifts.map(s => s._directorate)).size,
      byTier, byAgency, byDirectorate, byWard, byCategory, byReason,
      specialsBreakdown, cohortsBreakdown,
      specialsTotal: specialsShifts.length, cohortsTotal: cohortsShifts.length,
    };
  }, [filteredShifts]);

  const clearFilters = () => { setFDirectorate(''); setFWard(''); setFAgency(''); setFTier(''); };
  const hasFilters = fDirectorate || fWard || fAgency || fTier;

  // Chronological (not volume-sorted) monthly breakdown for the trend chart.
  const byMonth = useMemo(() => {
    const map = {};
    filteredShifts.forEach(s => {
      if (!s.startDateTime) return;
      const key = monthKey(s.startDateTime);
      if (!map[key]) map[key] = { total: 0, complete: 0, cancelled: 0, other: 0 };
      map[key].total++;
      if (s._status === 'Complete')       map[key].complete++;
      else if (s._status === 'Cancelled') map[key].cancelled++;
      else                                 map[key].other++;
    });
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, d]) => [monthLabel(key), d]);
  }, [filteredShifts]);

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w) return;

    const filterSummary = [
      `Period: ${startDate} to ${endDate}`,
      fDirectorate ? `Directorate: ${fDirectorate}` : '',
      fWard        ? `Ward: ${fWard}`               : '',
      fAgency      ? `Agency: ${fAgency}`           : '',
      fTier        ? `Tier: ${fTier}`               : '',
    ].filter(Boolean).join(' · ');

    const statBox = (label, value, sub, color) => `
      <div style="background:#f2f2f2;border:1px solid #d0d0d0;border-top:3px solid ${color};
        border-radius:8px;padding:14px 16px;min-width:120px;flex:1;">
        <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#555;
          margin-bottom:6px;font-family:'Courier New',monospace;">${label}</div>
        <div style="font-size:24px;font-weight:700;color:#111;line-height:1;
          font-family:'Courier New',monospace;">${value}</div>
        ${sub ? `<div style="font-size:9px;color:#555;margin-top:4px;
          font-family:'Courier New',monospace;">${sub}</div>` : ''}
      </div>`;

    const sectionTitle = (label) => `
      <div style="display:flex;align-items:center;gap:10px;margin:24px 0 12px;">
        <div style="width:4px;height:20px;background:#0a8577;border-radius:2px;flex-shrink:0;"></div>
        <div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;
          color:#0d1b2a;font-family:'Courier New',monospace;">${label}</div>
        <div style="flex:1;height:1px;background:#e0e7ef;"></div>
      </div>`;

    const barRow = (label, value, total, color) => {
      const p = total === 0 ? 0 : Math.round((value / total) * 100);
      return `<div style="margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
          <span style="font-size:10px;color:#0d1b2a;font-family:'Courier New',monospace;">${label}</span>
          <span style="font-size:10px;font-weight:700;color:#0d1b2a;font-family:'Courier New',monospace;">
            ${value} <span style="color:#8fa3b1;font-weight:400;">(${p}%)</span></span>
        </div>
        <div style="height:7px;background:#e8ecf0;border-radius:4px;overflow:hidden;margin-top:3px;">
          <div style="height:100%;width:${p}%;background:${color};border-radius:4px;"></div>
        </div></div>`;
    };

    const tableRows = (rows) => rows.map(([k, d]) => `
      <tr>
        <td>${k}</td><td>${d.total}</td><td>${d.complete}</td><td>${d.cancelled}</td>
        <td>${pct(d.complete, d.total)}%</td>
      </tr>`).join('');

    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>Agency Dashboard Report</title>
    <style>
      @page { size: A4 portrait; margin: 15mm 12mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Courier New', monospace; background: #fff; color: #0d1b2a;
        font-size: 11px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .flex-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
      .card { background: #f2f2f2; border: 1px solid #d0d0d0; border-radius: 8px; padding: 14px; }
      .label { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #555; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      th { background: #f0f4f8; padding: 7px 10px; text-align: left; font-weight: 700;
        border-bottom: 2px solid #e0e7ef; font-size: 9px; text-transform: uppercase; }
      td { padding: 6px 10px; border-bottom: 1px solid #f0f4f8; }
    </style></head><body>

    <div style="background:#0d1b2a;padding:20px 24px;border-radius:10px;margin-bottom:20px;">
      <div style="font-size:18px;font-weight:700;color:#f0f4f8;letter-spacing:1px;">Agency Dashboard</div>
      <div style="font-size:11px;color:#0cb89e;margin-top:4px;letter-spacing:1.5px;">
        BEAUMONT HOSPITAL · ALL DIRECTORATES</div>
      <div style="margin-top:14px;padding:10px 14px;background:rgba(255,255,255,0.07);
        border-radius:6px;border-left:3px solid #0a8577;">
        <div style="font-size:9px;color:#8fa3b1;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px;">Filters Applied</div>
        <div style="font-size:10px;color:#f0f4f8;">${filterSummary}</div>
      </div>
    </div>

    ${sectionTitle('1 · Overview')}
    <div class="flex-row">
      ${statBox('Total Agency Shifts', stats.total,     `${fmt(stats.totalMins)} scheduled`,          '#0a8577')}
      ${statBox('Complete',            stats.complete,  `${stats.pctComplete}%`,                       '#27ae60')}
      ${statBox('Cancelled',           stats.cancelled, `${stats.pctCancelled}%`,                       '#c0392b')}
      ${statBox('Not-Worked',          stats.notWorked, `${stats.pctNotWorked}%`,                       '#e8a020')}
      ${statBox('Open / Assigned',     stats.open,      `${stats.pctOpen}%`,                            '#2196f3')}
      ${statBox('Agencies Used',       stats.uniqueAgencies, `${stats.uniqueWards} wards · ${stats.uniqueDirectorates} directorates`, '#0cb89e')}
    </div>

    ${sectionTitle('2 · Shift Volume by Month')}
    <div class="card" style="margin-bottom:20px;">
      <div style="display:flex;align-items:flex-end;gap:8px;height:130px;overflow-x:auto;">
        ${(() => {
          const maxTotal = Math.max(1, ...byMonth.map(([, d]) => d.total));
          return byMonth.map(([label, d]) => {
            const h = Math.round((d.total / maxTotal) * 90);
            const ch = d.total === 0 ? 0 : Math.round((d.complete / d.total) * h);
            const xh = d.total === 0 ? 0 : Math.round((d.cancelled / d.total) * h);
            const oh = Math.max(0, h - ch - xh);
            return `
              <div style="display:flex;flex-direction:column;align-items:center;min-width:34px;flex:1;">
                <div style="font-size:9px;font-weight:700;margin-bottom:4px;">${d.total}</div>
                <div style="display:flex;flex-direction:column-reverse;width:22px;height:${h}px;border-radius:3px 3px 0 0;overflow:hidden;">
                  <div style="height:${ch}px;background:#27ae60;"></div>
                  <div style="height:${xh}px;background:#c0392b;"></div>
                  <div style="height:${oh}px;background:#e8a020;"></div>
                </div>
                <div style="font-size:8px;color:#555;margin-top:6px;">${label}</div>
              </div>`;
          }).join('');
        })()}
      </div>
    </div>

    ${sectionTitle('3 · By Agency Tier')}
    <div class="card" style="margin-bottom:20px;">
      ${stats.byTier.map(([tier, d]) => barRow(tier, d.total, stats.total, '#0a8577')).join('')}
    </div>

    ${sectionTitle('4 · Top Agencies')}
    <table style="margin-bottom:20px;">
      <thead><tr><th>Agency</th><th>Total</th><th>Complete</th><th>Cancelled</th><th>Complete %</th></tr></thead>
      <tbody>${tableRows(stats.byAgency)}</tbody>
    </table>

    ${sectionTitle('5 · By Directorate')}
    <table style="margin-bottom:20px;">
      <thead><tr><th>Directorate</th><th>Total</th><th>Complete</th><th>Cancelled</th><th>Complete %</th></tr></thead>
      <tbody>${tableRows(stats.byDirectorate)}</tbody>
    </table>

    ${sectionTitle('6 · By Ward')}
    <table style="margin-bottom:20px;">
      <thead><tr><th>Ward</th><th>Total</th><th>Complete</th><th>Cancelled</th><th>Complete %</th></tr></thead>
      <tbody>${tableRows(stats.byWard)}</tbody>
    </table>

    ${sectionTitle('7 · By Category & Reason for Request')}
    <div style="display:flex;gap:10px;margin-bottom:20px;">
      <div class="card" style="flex:1;">
        <div class="label" style="margin-bottom:10px;">By Category of Staff</div>
        ${stats.byCategory.map(([cat, d]) => barRow(cat, d.total, stats.total, '#0a8577')).join('')}
      </div>
      <div class="card" style="flex:1;">
        <div class="label" style="margin-bottom:10px;">By Reason for Request</div>
        ${stats.byReason.map(([reason, d]) =>
          barRow(reason, d.total, stats.total, reason === '1-1 special' ? '#f44336' : reason === 'Cohorts' ? '#ff9800' : '#0a8577')
        ).join('')}
      </div>
    </div>

    ${sectionTitle('8 · Clinical Reason Breakdown (1-1 Special & Cohorts)')}
    <div style="display:flex;gap:10px;margin-bottom:20px;">
      <div class="card" style="flex:1;">
        <div class="label" style="color:#f44336;margin-bottom:6px;">1-1 Special — Clinical Reason</div>
        <div style="font-size:9px;color:#555;margin-bottom:8px;">${stats.specialsTotal} total agency shifts</div>
        ${stats.specialsBreakdown.length === 0
          ? '<div style="font-size:10px;color:#8fa3b1;">No 1-1 special agency shifts in period</div>'
          : stats.specialsBreakdown.map(([reason, d]) =>
              barRow(reason, d.total, stats.specialsTotal || 1, reason.startsWith('⚠') ? '#e8a020' : '#f44336')
            ).join('')}
      </div>
      <div class="card" style="flex:1;">
        <div class="label" style="color:#ff9800;margin-bottom:6px;">Cohorts — Clinical Reason</div>
        <div style="font-size:9px;color:#555;margin-bottom:8px;">${stats.cohortsTotal} total agency shifts</div>
        ${stats.cohortsBreakdown.length === 0
          ? '<div style="font-size:10px;color:#8fa3b1;">No Cohorts agency shifts in period</div>'
          : stats.cohortsBreakdown.map(([reason, d]) =>
              barRow(reason, d.total, stats.cohortsTotal || 1, reason.startsWith('⚠') ? '#e8a020' : '#ff9800')
            ).join('')}
      </div>
    </div>

    ${sectionTitle('9 · PO Number Coverage')}
    <div class="flex-row" style="margin-bottom:10px;">
      ${statBox('Shifts with PO',    stats.withPOCount, `${stats.pctPO}% of total`, '#27ae60')}
      ${statBox('Missing PO',        stats.missingPO,   'Needs follow-up',          '#c0392b')}
    </div>

    <div style="border-top:2px solid #0d1b2a;padding-top:10px;display:flex;
      justify-content:space-between;align-items:center;margin-top:20px;">
      <div style="font-size:8px;color:#8fa3b1;">BEAUMONT HOSPITAL · AGENCY DASHBOARD</div>
      <div style="font-size:8px;color:#8fa3b1;">Generated ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
    </div>

    <script>window.onload=function(){setTimeout(()=>{window.print();setTimeout(()=>window.close(),200)},600)}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: NAVY, p: { xs: 2, md: 3 },
      fontFamily: '"DM Mono","Courier New",monospace' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');`}</style>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <AssessmentIcon sx={{ color: TEAL, fontSize: 28 }} />
            <Typography sx={{ fontFamily: '"DM Mono",monospace',
              fontSize: { xs: '1.2rem', md: '1.6rem' }, fontWeight: 700, color: WHITE, letterSpacing: 1 }}>
              Agency Dashboard
            </Typography>
          </Box>
          <Typography sx={{ color: MUTED, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace', letterSpacing: 1.5 }}>
            BEAUMONT HOSPITAL · ALL DIRECTORATES
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip label={`${stats.total} agency shifts`} size="small"
            sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, fontFamily: '"DM Mono",monospace',
              fontSize: '0.7rem', border: `1px solid ${TEAL}44` }} />
          <Chip label={`${stats.uniqueAgencies} agencies`} size="small"
            sx={{ bgcolor: `${AMBER}22`, color: AMBER, fontFamily: '"DM Mono",monospace',
              fontSize: '0.7rem', border: `1px solid ${AMBER}44` }} />
          <Tooltip title="Print report">
            <Button onClick={handlePrint} disabled={isLoading} size="small"
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, border: `1px solid ${TEAL}44`,
                fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', letterSpacing: 1,
                textTransform: 'uppercase', px: 1.5,
                '&:hover': { bgcolor: TEAL, color: WHITE } }}>
              Print Report
            </Button>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton onClick={refetch} size="small"
              sx={{ color: MUTED, '&:hover': { color: TEAL_L, bgcolor: `${TEAL}22` } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2, mb: 3 }}>
        <Typography sx={{ fontSize: '0.68rem', letterSpacing: 2, textTransform: 'uppercase',
          color: MUTED, fontFamily: '"DM Mono",monospace', mb: 1.5 }}>Filters</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="From" type="date" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE,
              '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } },
              '& input': { fontFamily: '"DM Mono",monospace', fontSize: '0.8rem' } }} />
          <TextField size="small" label="To" type="date" value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE,
              '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } },
              '& input': { fontFamily: '"DM Mono",monospace', fontSize: '0.8rem' } }} />
          <FilterSelect label="Directorate" value={fDirectorate}
            onChange={(v) => { setFDirectorate(v); setFWard(''); }} options={directorateOptions} />
          <FilterSelect label="Ward / Dept" value={fWard} onChange={setFWard} options={wardOptions} />
          <FilterSelect label="Agency" value={fAgency} onChange={setFAgency} options={agencyOptions} />
          <FilterSelect label="Tier" value={fTier} onChange={setFTier} options={tierOptions} />
          {hasFilters && (
            <Button size="small" onClick={clearFilters}
              sx={{ color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.7rem',
                '&:hover': { color: WHITE } }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      ) : stats.total === 0 ? (
        <Alert severity="info" sx={{ bgcolor: `${TEAL}11`, color: TEAL_L, border: `1px solid ${TEAL}33` }}>
          No agency shifts found for the selected period and filters.
        </Alert>
      ) : (
        <Box>

          {/* ══ SECTION 1: Overview ═════════════════════════════════════ */}
          <SectionTitle icon={AssessmentIcon} label="Overview" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Agency Shifts', value: stats.total,     sub: `${fmt(stats.totalMins)} scheduled`, color: TEAL,   icon: AssessmentIcon },
              { label: 'Complete',            value: stats.complete,  sub: `${stats.pctComplete}%`,             color: GREEN,  icon: CheckCircleOutlineIcon },
              { label: 'Cancelled',           value: stats.cancelled, sub: `${stats.pctCancelled}%`,            color: RED,    icon: CancelOutlinedIcon },
              { label: 'Not-Worked',          value: stats.notWorked, sub: `${stats.pctNotWorked}%`,            color: AMBER,  icon: EventBusyIcon },
              { label: 'Open / Assigned',     value: stats.open,      sub: `${stats.pctOpen}%`,                 color: BLUE,   icon: EventAvailableIcon },
              { label: 'Agencies Used',       value: stats.uniqueAgencies, sub: `${stats.uniqueWards} wards · ${stats.uniqueDirectorates} directorates`, color: TEAL_L, icon: BusinessCenterIcon },
            ].map(({ label, value, sub, color, icon }) => (
              <Grid item xs={6} sm={4} md={2} key={label}>
                <StatCard label={label} value={value} sub={sub} color={color} icon={icon} />
              </Grid>
            ))}
          </Grid>

          {/* ══ SECTION 2: Shift Volume by Month ═══════════════════════════ */}
          <SectionTitle icon={AssessmentIcon} label="Shift Volume by Month" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5 }}>
                {byMonth.length === 0 ? (
                  <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No data</Typography>
                ) : (
                  <>
                    <MonthlyTrendChart data={byMonth} />
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'center' }}>
                      {[['Complete', GREEN], ['Cancelled', RED], ['Other', AMBER]].map(([l, c]) => (
                        <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, bgcolor: c, borderRadius: '50%' }} />
                          <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: '"DM Mono",monospace' }}>{l}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 3: Status Breakdown ═════════════════════════════ */}
          <SectionTitle icon={CheckCircleOutlineIcon} label="Status Breakdown" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono",monospace', mb: 2 }}>Shift Outcomes</Typography>
                <BarRow label="Complete"        value={stats.complete}  total={stats.total} color={GREEN} />
                <BarRow label="Cancelled"       value={stats.cancelled} total={stats.total} color={RED} />
                <BarRow label="Not-Worked"      value={stats.notWorked} total={stats.total} color={AMBER} />
                <BarRow label="Open / Assigned" value={stats.open}      total={stats.total} color={BLUE} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
                <GaugeRing value={stats.pctComplete}  label="Complete"  color={GREEN} />
                <GaugeRing value={stats.pctCancelled} label="Cancelled" color={RED} />
                <GaugeRing value={stats.pctOpen}      label="Open"      color={BLUE} />
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 3: By Agency Tier ════════════════════════════════ */}
          <SectionTitle icon={WorkspacePremiumIcon} label="By Agency Tier" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5 }}>
                {stats.byTier.map(([tier, d]) => (
                  <StackedBarRow key={tier} label={tier} complete={d.complete} cancelled={d.cancelled} other={d.other} total={d.total} />
                ))}
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 4: Top Agencies ═══════════════════════════════════ */}
          <SectionTitle icon={GroupIcon} label="Top Agencies" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5 }}>
                {stats.byAgency.map(([agency, d]) => (
                  <StackedBarRow key={agency} label={agency} complete={d.complete} cancelled={d.cancelled} other={d.other} total={d.total} badge={d.tier} />
                ))}
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 5: By Directorate & Ward ══════════════════════════ */}
          <SectionTitle icon={LocationCityIcon} label="By Directorate & Ward" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono",monospace', mb: 2 }}>By Directorate</Typography>
                {stats.byDirectorate.map(([d, v]) => (
                  <StackedBarRow key={d} label={d} complete={v.complete} cancelled={v.cancelled} other={v.other} total={v.total} />
                ))}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono",monospace', mb: 2 }}>Top Wards (Agency Usage)</Typography>
                {stats.byWard.map(([ward, v]) => (
                  <StackedBarRow key={ward} label={ward} complete={v.complete} cancelled={v.cancelled} other={v.other} total={v.total} />
                ))}
              </Paper>
            </Grid>
          </Grid>

          {/* ══ SECTION 6: By Category & Reason for Request ═══════════════ */}
          {(stats.byCategory.length > 0 || stats.byReason.length > 0) && (
            <>
              <SectionTitle icon={GroupIcon} label="By Category & Reason for Request" />
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono",monospace', mb: 2 }}>By Category of Staff</Typography>
                    {stats.byCategory.length === 0
                      ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No data</Typography>
                      : stats.byCategory.map(([cat, d]) => (
                        <StackedBarRow key={cat} label={cat} complete={d.complete} cancelled={d.cancelled} other={d.other} total={d.total} />
                      ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid #f4433622`, borderRadius: 2, p: 2.5, height: '100%' }}>
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: '"DM Mono",monospace', mb: 2 }}>By Reason for Request</Typography>
                    {stats.byReason.length === 0
                      ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No data</Typography>
                      : stats.byReason.map(([reason, d]) => (
                        <StackedBarRow
                          key={reason}
                          label={reason}
                          complete={d.complete}
                          cancelled={d.cancelled}
                          other={d.other}
                          total={d.total}
                          labelColor={reason === '1-1 special' ? '#f44336' : reason === 'Cohorts' ? '#ff9800' : WHITE}
                        />
                      ))}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* ══ SECTION 7: Clinical Reason Breakdown ═══════════════════════ */}
          {(stats.specialsTotal > 0 || stats.cohortsTotal > 0) && (
            <>
              <SectionTitle icon={LocalHospitalIcon} label="Clinical Reason Breakdown" />
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: '1px solid #f4433633', borderRadius: 2, p: 2.5, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{ width: 3, height: 16, bgcolor: '#f44336', borderRadius: 1 }} />
                      <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: '#f44336', fontFamily: '"DM Mono",monospace' }}>
                        1-1 Special — Clinical Reason
                      </Typography>
                      <Chip label={`${stats.specialsTotal} total`} size="small"
                        sx={{ bgcolor: '#f4433622', color: '#f44336', fontFamily: '"DM Mono",monospace', fontSize: '0.62rem', border: '1px solid #f4433644', ml: 'auto' }} />
                    </Box>
                    {stats.specialsBreakdown.length === 0
                      ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No 1-1 special agency shifts in this period</Typography>
                      : stats.specialsBreakdown.map(([reason, d]) => (
                        <StackedBarRow key={reason} label={reason} complete={d.complete} cancelled={d.cancelled} other={d.other} total={d.total}
                          labelColor={reason.startsWith('⚠') ? AMBER : WHITE} />
                      ))}
                    {stats.specialsBreakdown.some(([r]) => r.startsWith('⚠')) && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: `${AMBER}11`, borderRadius: 1, border: `1px solid ${AMBER}33` }}>
                        <Typography sx={{ fontSize: '0.65rem', color: AMBER, fontFamily: '"DM Mono",monospace' }}>
                          △ Some 1-1 special agency shifts have no clinical reason recorded
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ bgcolor: SLATE, border: '1px solid #ff980033', borderRadius: 2, p: 2.5, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{ width: 3, height: 16, bgcolor: '#ff9800', borderRadius: 1 }} />
                      <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: '#ff9800', fontFamily: '"DM Mono",monospace' }}>
                        Cohorts — Clinical Reason
                      </Typography>
                      <Chip label={`${stats.cohortsTotal} total`} size="small"
                        sx={{ bgcolor: '#ff980022', color: '#ff9800', fontFamily: '"DM Mono",monospace', fontSize: '0.62rem', border: '1px solid #ff980044', ml: 'auto' }} />
                    </Box>
                    {stats.cohortsBreakdown.length === 0
                      ? <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No Cohorts agency shifts in this period</Typography>
                      : stats.cohortsBreakdown.map(([reason, d]) => (
                        <StackedBarRow key={reason} label={reason} complete={d.complete} cancelled={d.cancelled} other={d.other} total={d.total}
                          labelColor={reason.startsWith('⚠') ? AMBER : WHITE} />
                      ))}
                    {stats.cohortsBreakdown.some(([r]) => r.startsWith('⚠')) && (
                      <Box sx={{ mt: 1.5, p: 1, bgcolor: `${AMBER}11`, borderRadius: 1, border: `1px solid ${AMBER}33` }}>
                        <Typography sx={{ fontSize: '0.65rem', color: AMBER, fontFamily: '"DM Mono",monospace' }}>
                          △ Some Cohorts agency shifts have no clinical reason recorded
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* ══ SECTION 8: PO Number Data Quality ═════════════════════════ */}
          <SectionTitle icon={ReceiptLongIcon} label="PO Number Coverage" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <GaugeRing value={stats.pctPO} label="Shifts with PO Number"
                  color={stats.pctPO >= 90 ? GREEN : stats.pctPO >= 70 ? AMBER : RED} size={110} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontFamily: '"DM Mono",monospace' }}>Shifts with PO recorded</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: GREEN, fontFamily: '"DM Mono",monospace', fontWeight: 700 }}>{stats.withPOCount}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: MUTED, fontFamily: '"DM Mono",monospace' }}>Missing PO number</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: RED, fontFamily: '"DM Mono",monospace', fontWeight: 700 }}>{stats.missingPO}</Typography>
                </Box>
                {stats.missingPO > 0 && (
                  <Alert severity="warning" sx={{ bgcolor: `${AMBER}11`, color: AMBER, border: `1px solid ${AMBER}33`,
                    fontFamily: '"DM Mono",monospace', fontSize: '0.72rem' }}>
                    {stats.missingPO} agency shift{stats.missingPO > 1 ? 's are' : ' is'} missing a PO number — follow up before invoicing.
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', color: `${MUTED}88`, fontFamily: '"DM Mono",monospace', letterSpacing: 1 }}>
              BEAUMONT HOSPITAL · AGENCY DASHBOARD · DATA REFLECTS SELECTED DATE RANGE AND FILTERS · {new Date().toLocaleString()}
            </Typography>
          </Box>

        </Box>
      )}
    </Box>
  );
};

export default AgencyDashboard;